import React from 'react';
import { useParams } from 'react-router-dom';
import { getBranches } from '../services/branchesApi';
import { createOrder } from '../services/ordersApi';
import { sendMarketingOrderSms, verifySms } from '../services/authApi';
import { getDistricts, getRegions } from '../services/regionsApi';
import {
  branchIdFromSlug,
  findBranchIdByShopSlug,
  getPublicBranchProducts,
} from '../services/marketingApi';
import { getMarketingShopSubdomainFromHost } from '../utils/marketingShopHost';
import { usePublicSeoShopOverride } from '../seo/PublicSeoContext';
import { resolveMediaUrl } from '../utils/resolveMediaUrl';
import './BranchShopPage.css';
import { formatMoneyUz } from '../utils/formatMoneyUz';
import { Select } from '../components/ui/select';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageDropdown } from '../components/LanguageDropdown';

type SaleChoice = {
  key: string;
  saleUnit: string;
  price: number;
  label?: string;
  color?: string;
  stock?: number;
  variantId?: string | number | null;
  /** 1 quti / optom partiyasida nechta dona (yoki boshqa bazaviy miqdor) */
  unitFactor?: number;
};

type Product = {
  id: string;
  name: string;
  salePrice: number;
  quantity: number;
  imageUrl?: string;
  barcode?: string;
  marketingTitle?: string;
  marketingKeywords?: string;
  saleChoices?: SaleChoice[];
  saleUnit?: string;
};

type CartLine = Product & {
  /** Savat qatori kaliti (variant bilan `productId__saleOptionKey`) */
  qty: number;
  /** Katalog `inventory_items.id` — buyurtmada `product_id` */
  productId: string;
  /** Variant / vitrina qoldig‘i — miqdor shu chegaradan oshmasin */
  maxStock: number;
  /** Quti/optom: backend `unit_factor` */
  lineUnitFactor: number;
  selectedChoiceKey?: string;
  variantLabel?: string;
  unitPrice: number;
  variantId?: string | number | null;
};
const PHONE_PREFIX = '+998';
const PHONE_LOCAL_LEN = 9;
const SMS_OTP_LENGTH = 4;

function formatUzs(n: number) {
  return formatMoneyUz(Math.max(0, n));
}

/** Qo'lda yoki paste: faqat 9 ta mahalliy raqam (998 prefiksini tashlab). */
function parsePhoneLocalInput(raw: string): string {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.startsWith('998') && d.length >= 12) {
    d = d.slice(3, 12);
  } else if (d.length > PHONE_LOCAL_LEN) {
    d = d.slice(0, PHONE_LOCAL_LEN);
  }
  return d.slice(0, PHONE_LOCAL_LEN);
}

/** 50 777 76 67 — 2+3+2+2 */
function formatLocalPhoneDisplay(localDigits: string): string {
  const d = localDigits.slice(0, PHONE_LOCAL_LEN);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)} ${d.slice(2)}`;
  if (d.length <= 7) return `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5)}`;
  return `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5, 7)} ${d.slice(7, 9)}`;
}

function fullPhoneFromLocalDigits(localDigits: string): string {
  const digits = parsePhoneLocalInput(localDigits);
  return `${PHONE_PREFIX}${digits}`;
}

/**
 * Backend ba'zan expires_in ni millisekund (120000) yoki float soniyada yuborishi mumkin.
 * Juda katta "soniya" qiymatlari odatda noto'g'ri — ms sifatida qayta talqin qilinadi.
 */
function parseOtpExpiresInSeconds(raw: unknown): number {
  let n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  n = Math.floor(n);
  if (n >= 1000 && n < 600_000) {
    const asSec = Math.floor(n / 1000);
    if (asSec >= 1 && asSec <= 600) return asSec;
  }
  if (n >= 1 && n <= 600) return n;
  if (n > 600) {
    const asSec = Math.floor(n / 1000);
    if (asSec >= 1 && asSec <= 600) return asSec;
    return Math.min(n, 600);
  }
  return n;
}

function formatMmSs(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60)
    .toString()
    .padStart(2, '0');
  const ss = (s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

function mapSmsErrorCodeToMessage(code: string, t: (key: string) => string): string {
  switch (code) {
    case 'PHONE_INVALID':
      return t('branchShop_err_phone_invalid');
    case 'SMS_CODE_INVALID':
      return t('branchShop_err_sms_code_invalid');
    case 'SMS_CODE_EXPIRED':
      return t('branchShop_err_sms_code_expired');
    case 'SMS_NOT_VERIFIED':
      return t('branchShop_err_sms_not_verified');
    case 'VERIFICATION_NOT_FOUND':
      return t('branchShop_err_verification_not_found');
    case 'BRANCH_ID_INVALID':
      return t('branchShop_err_branch_id_invalid');
    default:
      return '';
  }
}

function fillTpl(template: string, vars: Record<string, string | number>): string {
  let out = template;
  for (const [k, v] of Object.entries(vars)) {
    out = out.split(`{${k}}`).join(String(v));
  }
  return out;
}

function digitsOnlyOtp(raw: unknown, maxLen: number): string {
  if (raw == null) return '';
  const s = String(raw).trim().replace(/\D/g, '');
  if (!s) return '';
  return s.slice(0, maxLen);
}

function getDataObject(root: unknown): Record<string, unknown> | null {
  if (!root || typeof root !== 'object' || Array.isArray(root)) return null;
  const d = (root as Record<string, unknown>).data;
  if (d != null && typeof d === 'object' && !Array.isArray(d)) return d as Record<string, unknown>;
  return null;
}

/**
 * send-sms: avval root.verification_code, bo‘lmasa data.verification_code.
 * Productionda kod bo‘lmasligi normal; dev/staging yoki SMS_RETURN_CODE_IN_RESPONSE=true da tekshiring.
 */
function pickSendSmsVerificationCode(root: unknown): string {
  if (!root || typeof root !== 'object' || Array.isArray(root)) return '';
  const r = root as Record<string, unknown>;
  let raw = r.verification_code ?? r.verificationCode;
  let digits = digitsOnlyOtp(raw, 6);
  if (digits.length >= 4 && digits.length <= 6) return digits;
  const data = getDataObject(r);
  if (!data) return '';
  raw = data.verification_code ?? data.verificationCode;
  digits = digitsOnlyOtp(raw, 6);
  return digits.length >= 4 && digits.length <= 6 ? digits : '';
}

/** Root, keyin data — verification_id / TTL kalitlari. */
function pickFirstPresent(root: unknown, keys: string[]): string {
  if (!root || typeof root !== 'object' || Array.isArray(root)) return '';
  const r = root as Record<string, unknown>;
  for (const k of keys) {
    const v = r[k];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  const data = getDataObject(r);
  if (!data) return '';
  for (const k of keys) {
    const v = data[k];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
}

/** expires_in / retry_after — butun soniya; avval root, keyin data. */
function pickOtpTtlSeconds(root: unknown): number {
  if (!root || typeof root !== 'object' || Array.isArray(root)) return 0;
  const r = root as Record<string, unknown>;
  const ttlKeys = ['retry_after', 'retryAfter', 'expires_in', 'expiresIn'];
  for (const k of ttlKeys) {
    const sec = parseOtpExpiresInSeconds(r[k]);
    if (sec > 0) return sec;
  }
  const data = getDataObject(r);
  if (!data) return 0;
  for (const k of ttlKeys) {
    const sec = parseOtpExpiresInSeconds(data[k]);
    if (sec > 0) return sec;
  }
  return 0;
}

function pickApiErrorCode(root: unknown): string {
  if (!root || typeof root !== 'object' || Array.isArray(root)) return '';
  const r = root as Record<string, unknown>;
  if (r.code != null) return String(r.code).trim();
  const data = getDataObject(r);
  if (data?.code != null) return String(data.code).trim();
  return '';
}

/** verify-sms: verified / verify_token — root, keyin data. */
function pickVerifySmsVerified(root: unknown): boolean {
  if (!root || typeof root !== 'object' || Array.isArray(root)) return false;
  const r = root as Record<string, unknown>;
  if (r.verified === true) return true;
  const data = getDataObject(r);
  return data?.verified === true;
}

function pickVerifyToken(root: unknown): string {
  return pickFirstPresent(root, ['verify_token', 'verifyToken']);
}

/** Backend `sale_unit` kodlari — vitrinada tarjima qilingan ko‘rinish */
function saleUnitTitleForShop(saleUnit: string, t: (key: string) => string): string {
  const map: Record<string, string> = {
    piece: 'product_sale_unit_piece',
    kg: 'product_sale_unit_kg_short',
    meter: 'product_sale_unit_meter_short',
    liter: 'product_sale_unit_liter_short',
    box: 'product_sale_unit_box',
    pack: 'product_sale_unit_pack',
    roll: 'product_sale_unit_roll',
    set: 'product_sale_unit_set',
    pallet: 'product_sale_unit_pallet',
    ton: 'product_sale_unit_ton_short',
    sqm: 'product_sale_unit_sqm_short',
    cubic_meter: 'product_sale_unit_cubic_meter_short',
    cm: 'product_sale_unit_cm_short',
    mm: 'product_sale_unit_mm_short',
    pair: 'product_sale_unit_pair_short',
    size: 'branchShop_variant_default',
    wholesale: 'product_sale_unit_wholesale_short',
  };
  const u = String(saleUnit || '').trim().toLowerCase();
  const key = map[u];
  if (key) return t(key);
  if (!u) return t('product_sale_unit_piece');
  return String(saleUnit).trim();
}

function saleChoicePrimaryTitle(sc: SaleChoice, t: (key: string) => string): string {
  if (String(sc.saleUnit).toLowerCase() === 'size') {
    const lb = String(sc.label ?? '').trim();
    const col = String(sc.color ?? '').trim();
    const title = `${lb}${col ? (lb ? ' / ' : '') + col : ''}`.trim();
    return title || t('branchShop_variant_default');
  }
  const raw = String(sc.label ?? '').trim();
  if (raw && raw.toLowerCase() !== String(sc.saleUnit).toLowerCase()) {
    return raw;
  }
  return saleUnitTitleForShop(sc.saleUnit, t);
}

function saleChoiceFactorHint(sc: SaleChoice, t: (key: string) => string): string | undefined {
  const n = sc.unitFactor != null && Number.isFinite(Number(sc.unitFactor)) ? Math.max(1, Math.round(Number(sc.unitFactor))) : 1;
  if (n <= 1) return undefined;
  const u = String(sc.saleUnit || '').toLowerCase();
  const discrete = ['piece', 'box', 'pack', 'set', 'roll', 'pallet', 'pair', 'wholesale'].includes(u);
  if (!discrete) return undefined;
  return fillTpl(t('branchShop_unit_factor_hint'), { n });
}

/**
 * Vitrina API: sale_options / saleChoices — narx 0 bo‘lgan qatorlar ham (masalan, asosiy + kg).
 * Bo‘sh keylarni to‘ldiramiz; hammasi 0 bo‘lsa — bitta «asosiy» qator (savdo modali uchun).
 */
function normalizeShopSaleChoices(raw: unknown[], baseUnit: string, basePrice: number): SaleChoice[] {
  const bu = String(baseUnit || 'piece').trim() || 'piece';
  const bp = Math.max(0, Math.round(Number(basePrice) || 0));
  const src = Array.isArray(raw) ? raw : [];
  const rows: SaleChoice[] = src.map((c: any, idx: number) => {
    const saleUnit = String(c.sale_unit ?? c.saleUnit ?? bu).trim() || bu;
    const price = Math.max(0, Math.round(Number(c.price ?? c.sale_price ?? 0)));
    let key = String(c.key ?? c.mode ?? '').trim();
    if (!key) key = `${saleUnit}-${idx}`;
    const ufRaw = c.unit_factor ?? c.unitFactor;
    const unitFactor =
      ufRaw != null && String(ufRaw).trim() !== '' && Number.isFinite(Number(ufRaw))
        ? Math.max(1, Math.round(Number(ufRaw)))
        : 1;
    return {
      key,
      saleUnit,
      price,
      label: c.label != null && String(c.label).trim() !== '' ? String(c.label) : undefined,
      color: c.color != null && String(c.color).trim() !== '' ? String(c.color) : undefined,
      stock: c.stock != null ? Number(c.stock) : undefined,
      variantId: c.variant_id ?? c.variantId ?? null,
      unitFactor,
    };
  }).filter((c) => Number.isFinite(c.price) && c.price >= 0);

  const dedup: SaleChoice[] = [];
  const seen = new Set<string>();
  for (const c of rows) {
    const sig = `${String(c.key).toLowerCase()}::${String(c.saleUnit).toLowerCase()}`;
    if (seen.has(sig)) continue;
    seen.add(sig);
    dedup.push(c);
  }
  if (dedup.length > 0) return dedup;
  if (bp > 0) {
    return [
      {
        key: 'primary',
        saleUnit: bu,
        price: bp,
        label: undefined,
        color: undefined,
        stock: undefined,
        variantId: null,
        unitFactor: 1,
      },
    ];
  }
  return [];
}

/** Laravel meta: branch_id / branch_name (camelCase ham bo‘lishi mumkin) */
type BranchProductsResponseMeta = {
  branch_id?: string | number | null;
  branch_name?: string | null;
};

function coalesceBranchProductsMeta(meta: unknown): BranchProductsResponseMeta | null {
  if (meta == null || typeof meta !== 'object' || Array.isArray(meta)) return null;
  const o = meta as Record<string, unknown>;
  return {
    branch_id: (o.branch_id ?? o.branchId) as string | number | null | undefined,
    branch_name: (o.branch_name ?? o.branchName) as string | null | undefined,
  };
}

/** Vitrina: variantda `stock` bo‘lsa shu, aks holda mahsulot `quantity_remaining` */
function maxStockForCartLine(p: Product, choice: SaleChoice | null | undefined): number {
  const baseQ = Math.max(0, Math.floor(Number(p.quantity) || 0));
  if (!choice) return baseQ;
  if (choice.stock != null && Number.isFinite(Number(choice.stock))) {
    return Math.max(0, Math.floor(Number(choice.stock)));
  }
  return baseQ;
}

function clampCartLineQty(line: CartLine, q: number): number {
  const cap =
    typeof line.maxStock === 'number' && line.maxStock > 0
      ? line.maxStock
      : Math.max(0, Math.floor(Number(line.quantity) || 0));
  if (cap <= 0) return 0;
  const n = Math.floor(Number(q));
  if (!Number.isFinite(n)) return 1;
  if (n < 1) return 1;
  return Math.min(n, cap);
}

export default function BranchShopPage() {
  const { t } = useLanguage();
  const { branchSlug } = useParams<{ branchSlug?: string }>();
  const hostSub =
    typeof window !== 'undefined' ? getMarketingShopSubdomainFromHost(window.location.hostname) : '';
  const slug = hostSub || branchSlug || '';
  const [resolvedBranchId, setResolvedBranchId] = React.useState(() => (slug ? branchIdFromSlug(slug) : ''));
  const [branchName, setBranchName] = React.useState('');
  const [products, setProducts] = React.useState<Product[]>([]);
  const [productFilterQuery, setProductFilterQuery] = React.useState('');
  const [cart, setCart] = React.useState<CartLine[]>([]);
  /** Miqdor maydonida vaqtincha bo‘sh qator (1 ni o‘chirish mumkin) — blur da jamlanadi */
  const [qtyDraftByLineId, setQtyDraftByLineId] = React.useState<Record<string, string>>({});
  const [name, setName] = React.useState('');
  const [phoneLocal, setPhoneLocal] = React.useState('');
  const [regionId, setRegionId] = React.useState('');
  const [districtId, setDistrictId] = React.useState('');
  const [regions, setRegions] = React.useState<Array<{ id: string; name: string }>>([]);
  const [districts, setDistricts] = React.useState<Array<{ id: string; name: string }>>([]);
  const [regionsLoading, setRegionsLoading] = React.useState(false);
  const [districtsLoading, setDistrictsLoading] = React.useState(false);
  const [address, setAddress] = React.useState('');
  const [deliveryMode, setDeliveryMode] = React.useState<'pickup' | 'delivery'>('pickup');
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [smsSending, setSmsSending] = React.useState(false);
  const [smsCode, setSmsCode] = React.useState('');
  const [verificationCode, setVerificationCode] = React.useState('');
  const [verificationId, setVerificationId] = React.useState('');
  const [verifyToken, setVerifyToken] = React.useState('');
  const [secondsLeft, setSecondsLeft] = React.useState(0);
  const [smsVerified, setSmsVerified] = React.useState(false);
  const [smsRequested, setSmsRequested] = React.useState(false);
  const [ok, setOk] = React.useState('');
  const [submitError, setSubmitError] = React.useState('');
  const [smsError, setSmsError] = React.useState('');
  const [cartModalOpen, setCartModalOpen] = React.useState(false);
  const [codeCopied, setCodeCopied] = React.useState(false);
  const copyCodeTimeoutRef = React.useRef<number>(0);
  const [variantPickerProduct, setVariantPickerProduct] = React.useState<Product | null>(null);

  const { setShopSeoOverride } = usePublicSeoShopOverride();
  React.useEffect(() => {
    const label = (branchName || '').trim() || (slug || '').trim();
    if (!label) {
      setShopSeoOverride(null);
      return undefined;
    }
    setShopSeoOverride({ type: 'shop', branchLabel: label });
    return () => setShopSeoOverride(null);
  }, [branchName, slug, setShopSeoOverride]);

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const total = cart.reduce((s, i) => s + i.qty * i.salePrice, 0);

  const filteredProducts = React.useMemo(() => {
    const q = productFilterQuery.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => {
      const name = (p.name ?? '').toLowerCase();
      const bar = (p.barcode ?? '').toLowerCase();
      const mt = (p.marketingTitle ?? '').toLowerCase();
      const mk = (p.marketingKeywords ?? '').toLowerCase();
      const id = String(p.id ?? '').toLowerCase();
      return name.includes(q) || bar.includes(q) || mt.includes(q) || mk.includes(q) || id.includes(q);
    });
  }, [products, productFilterQuery]);

  React.useEffect(() => {
    if (!secondsLeft) return;
    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => {
        const cur = Math.max(0, Math.floor(prev));
        return cur > 0 ? cur - 1 : 0;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [secondsLeft]);

  React.useEffect(() => {
    return () => window.clearTimeout(copyCodeTimeoutRef.current);
  }, []);

  const copyVerificationCode = async () => {
    if (!verificationCode) return;
    try {
      await navigator.clipboard.writeText(verificationCode);
      setCodeCopied(true);
      window.clearTimeout(copyCodeTimeoutRef.current);
      copyCodeTimeoutRef.current = window.setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      setCodeCopied(false);
    }
  };

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      if (!slug) {
        if (!cancelled) {
          setResolvedBranchId('');
          setBranchName('');
          setProducts([]);
          setLoading(false);
        }
        return;
      }
      let id = branchIdFromSlug(slug);
      let nameGuess: string | null = null;
      try {
        try {
          const bList = await getBranches({});
          if (!cancelled && bList?.length) {
            const matched = findBranchIdByShopSlug(slug, bList);
            if (matched) id = matched;
            const b = bList.find((x: any) => String(x.id) === String(id));
            if (b?.name) nameGuess = String(b.name);
          }
        } catch {
          /* public vitrina — JWT bo‘lmasa ham ishlaydi */
        }
        const result = await getPublicBranchProducts(id, { slug });
        if (cancelled) return;
        const rawList = result?.products ?? [];
        const meta = coalesceBranchProductsMeta(result?.meta ?? null);
        if (meta?.branch_id != null) id = String(meta.branch_id);
        if (meta?.branch_name != null && String(meta.branch_name).trim() !== '') {
          setBranchName(String(meta.branch_name).trim());
        }
        setResolvedBranchId(id);
        const normalizedProducts = (Array.isArray(rawList) ? rawList : []).map((p: any) => {
          const rawChoices = Array.isArray(p.saleChoices) ? p.saleChoices
            : Array.isArray(p.sale_options) ? p.sale_options
            : [];
          const baseUnit = String(p.sale_unit ?? p.saleUnit ?? 'piece');
          const basePrice = Math.max(0, Math.round(Number(p.sale_price ?? p.salePrice ?? 0)));
          const saleChoices = normalizeShopSaleChoices(rawChoices, baseUnit, basePrice);
          return {
            id: String(p.product_id ?? p.id ?? ''),
            name: String(p.name ?? ''),
            salePrice: basePrice,
            quantity: Number(p.quantity_remaining ?? p.quantity ?? 0),
            imageUrl: p.imageUrl ?? p.image_url ?? p.image ?? undefined,
            barcode: p.barcode ?? undefined,
            marketingTitle: p.marketingTitle ?? p.marketing_title ?? undefined,
            marketingKeywords: p.marketingKeywords ?? p.marketing_keywords ?? undefined,
            saleUnit: baseUnit,
            saleChoices: saleChoices.length > 0 ? saleChoices : undefined,
          } as Product;
        });
        setProducts(normalizedProducts);
      } catch {
        if (!cancelled) {
          setProducts([]);
          setBranchName(t('branchShop_branch_default'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, hostSub]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setRegionsLoading(true);
      try {
        const list = await getRegions({});
        if (cancelled) return;
        setRegions(
          (Array.isArray(list) ? list : []).map((r: any) => ({
            id: String(r.id ?? ''),
            name: String(r.name ?? r.display_name ?? r.name_uz ?? '—'),
          })),
        );
      } finally {
        if (!cancelled) setRegionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    if (!regionId) {
      setDistricts([]);
      setDistrictId('');
      return;
    }
    (async () => {
      setDistrictsLoading(true);
      try {
        const list = await getDistricts(regionId);
        if (cancelled) return;
        const mapped = (Array.isArray(list) ? list : []).map((d: any) => ({
          id: String(d.id ?? ''),
          name: String(d.name ?? d.display_name ?? d.name_uz ?? '—'),
        }));
        setDistricts(mapped);
        setDistrictId((prev) => (mapped.some((x) => x.id === prev) ? prev : ''));
      } finally {
        if (!cancelled) setDistrictsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [regionId]);

  const addToCart = (p: Product, choice?: SaleChoice) => {
    const baseProductId = String(p.id);
    const effectiveChoice = choice ?? p.saleChoices?.[0];
    const unitPrice = effectiveChoice?.price ?? p.salePrice;
    const choiceKey = effectiveChoice?.key;
    const variantLabel = effectiveChoice
      ? [effectiveChoice.label, effectiveChoice.color].filter((x) => String(x ?? '').trim() !== '').join(' / ')
      : '';
    const cartId = choiceKey ? `${baseProductId}__${choiceKey}` : baseProductId;
    const maxQ = maxStockForCartLine(p, effectiveChoice ?? null);
    const lineUnitFactor =
      effectiveChoice != null && Number.isFinite(Number(effectiveChoice.unitFactor))
        ? Math.max(1, Math.round(Number(effectiveChoice.unitFactor)))
        : 1;
    const lineSaleUnit = effectiveChoice?.saleUnit ?? p.saleUnit ?? 'piece';
    setCart((prev) => {
      const idx = prev.findIndex((x) => x.id === cartId);
      if (idx === -1) {
        if (maxQ <= 0) return prev;
        return [
          ...prev,
          {
            ...p,
            productId: baseProductId,
            id: cartId,
            unitPrice,
            salePrice: unitPrice,
            saleUnit: lineSaleUnit,
            qty: 1,
            maxStock: maxQ,
            lineUnitFactor,
            selectedChoiceKey: choiceKey,
            variantId: effectiveChoice?.variantId ?? null,
            variantLabel: variantLabel || undefined,
          },
        ];
      }
      if (maxQ <= 0) return prev;
      const next = [...prev];
      const capped = Math.min(next[idx].qty + 1, maxQ);
      if (capped === next[idx].qty) return prev;
      next[idx] = {
        ...next[idx],
        qty: capped,
        maxStock: maxQ,
        lineUnitFactor,
        saleUnit: lineSaleUnit,
      };
      return next;
    });
  };

  /** Katalogda sale_options bo‘lsa — har doim modaldan tanlash (bitta variant/o‘lchov ham). */
  const handleAddToCart = (p: Product) => {
    const choices = p.saleChoices ?? [];
    if (choices.length >= 1) {
      setVariantPickerProduct(p);
      return;
    }
    addToCart(p);
  };

  const removeLine = (id: string) => {
    setQtyDraftByLineId((d) => {
      if (!(id in d)) return d;
      const next = { ...d };
      delete next[id];
      return next;
    });
    setCart((prev) => prev.filter((x) => x.id !== id));
  };

  const requestSms = async () => {
    const digits = parsePhoneLocalInput(phoneLocal);
    const fullPhone = fullPhoneFromLocalDigits(phoneLocal);
    const needsAddress = deliveryMode === 'delivery';
    if (!name.trim() || digits.length !== PHONE_LOCAL_LEN || (needsAddress && (!regionId || !districtId || !address.trim())) || cart.length === 0) return;
    setSmsSending(true);
    setSmsError('');
    setSubmitError('');
    try {
      const response = await sendMarketingOrderSms(fullPhone);
      const root = response?.data ?? {};
      const normalizedCode = pickSendSmsVerificationCode(root);
      const nextVerificationId = pickFirstPresent(root, ['verification_id', 'verificationId']);
      const ttl = pickOtpTtlSeconds(root);
      setVerificationCode(normalizedCode);
      if (normalizedCode) setSmsCode(normalizedCode);
      if (nextVerificationId) setVerificationId(nextVerificationId);
      if (ttl > 0) setSecondsLeft(ttl);
      setSmsRequested(true);
      setSmsVerified(false);
      setVerifyToken('');
    } catch (err: any) {
      const errorRoot = err?.response?.data ?? {};
      const errorCode = pickApiErrorCode(errorRoot);
      if (errorCode === 'SMS_RATE_LIMITED') {
        const fallbackVerificationId = pickFirstPresent(errorRoot, ['verification_id', 'verificationId']);
        const ttl = pickOtpTtlSeconds(errorRoot);
        const rateCode = pickSendSmsVerificationCode(errorRoot);
        if (rateCode) {
          setVerificationCode(rateCode);
          setSmsCode(rateCode);
        }
        if (fallbackVerificationId && !verificationId) setVerificationId(fallbackVerificationId);
        if (ttl > 0) setSecondsLeft(ttl);
        setSmsRequested(true);
        setSmsError('');
      } else {
        setSmsError(
          mapSmsErrorCodeToMessage(errorCode, t) ||
            pickFirstPresent(errorRoot, ['message', 'error', 'msg']) ||
            err?.message ||
            t('branchShop_sms_failed'),
        );
      }
    } finally {
      setSmsSending(false);
    }
  };

  const confirmSms = async (): Promise<string | null> => {
    const code = smsCode.replace(/\D/g, '').slice(0, SMS_OTP_LENGTH);
    if (code.length !== SMS_OTP_LENGTH) {
      setSmsError(`SMS kod ${SMS_OTP_LENGTH} xonali bo‘lishi kerak.`);
      return null;
    }
    if (!verificationId) {
      setSmsError(t('branchShop_sms_not_sent'));
      return null;
    }
    try {
      const fullPhone = fullPhoneFromLocalDigits(phoneLocal);
      const res = await verifySms(fullPhone, code, verificationId, 'marketing_order');
      const body = res?.data ?? {};
      const verified = pickVerifySmsVerified(body);
      const token = pickVerifyToken(body);
      if (!verified || !token) {
        setSmsError(t('branchShop_verify_failed'));
        return null;
      }
      setVerifyToken(token);
      setSmsVerified(true);
      setSmsError('');
      setSubmitError('');
      return token;
    } catch (err: any) {
      const errBody = err?.response?.data ?? {};
      const errorCode = pickApiErrorCode(errBody);
      setSmsError(
        mapSmsErrorCodeToMessage(errorCode, t) ||
          pickFirstPresent(errBody, ['message', 'error', 'msg']) ||
          err?.message ||
          t('branchShop_code_verify_failed'),
      );
      return null;
    }
  };

  const handleConfirmPhone = async () => {
    const confirmedToken = await confirmSms();
    if (confirmedToken) {
      setSubmitError('');
    }
  };

  const submit = async () => {
    const digits = parsePhoneLocalInput(phoneLocal);
    const needsAddress = deliveryMode === 'delivery';
    if (!name.trim() || digits.length !== PHONE_LOCAL_LEN || (needsAddress && (!regionId || !districtId || !address.trim())) || cart.length === 0) return;
    if (!smsRequested) {
      await requestSms();
      return;
    }
    if (!smsVerified) {
      await handleConfirmPhone();
      return;
    }
    const tokenToUse = verifyToken;
    if (!tokenToUse) {
      setSubmitError(t('branchShop_sms_not_verified_submit'));
      return;
    }
    const branchNum = Number(String(resolvedBranchId || '').trim());
    if (!Number.isFinite(branchNum) || branchNum <= 0) {
      setSubmitError(t('branchShop_branch_unknown'));
      return;
    }
    const orderPhone = fullPhoneFromLocalDigits(phoneLocal);
    setSubmitting(true);
    setSubmitError('');
    try {
      const effectiveCart = cart
        .map((line) => {
          const d = qtyDraftByLineId[line.id];
          if (d === undefined) return line;
          const trimmed = String(d).trim();
          const parsed = trimmed === '' ? NaN : parseFloat(trimmed.replace(',', '.'));
          const qBase = Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : line.qty;
          return { ...line, qty: clampCartLineQty(line, qBase) };
        })
        .filter((line) => line.qty >= 1);
      setCart(effectiveCart);
      setQtyDraftByLineId({});
      if (effectiveCart.length === 0) {
        setSubmitError(t('branchShop_cart_invalid'));
        setSubmitting(false);
        return;
      }
      const orderTotal = effectiveCart.reduce((s, i) => s + i.qty * i.salePrice, 0);
      const regionName = deliveryMode === 'delivery' ? (regions.find((r) => r.id === regionId)?.name || regionId) : '';
      const districtName = deliveryMode === 'delivery' ? (districts.find((d) => d.id === districtId)?.name || districtId) : '';
      const noteLines = [
        `Filial: ${branchName} (${resolvedBranchId})`,
        `Buyurtma turi: ${deliveryMode === 'delivery' ? 'Yetkazib berish' : 'Olib ketish'}`,
        ...(deliveryMode === 'delivery' ? [`Viloyat: ${regionName}`, `Tuman: ${districtName}`, `Manzil: ${address.trim()}`] : []),
        'Mahsulotlar:',
        ...effectiveCart.map((i) => `- ${i.name}${i.variantLabel ? ` [${i.variantLabel}]` : ''} (${i.qty} x ${i.unitPrice ?? i.salePrice}) = ${i.qty * (i.unitPrice ?? i.salePrice)}`),
      ];
      const created = await createOrder({
        clientName: name.trim(),
        clientPhone: orderPhone,
        regionId,
        districtId,
        region: regionName,
        district: districtName,
        source: 'marketing_web',
        purpose: 'marketing_order',
        verify_token: tokenToUse,
        status: 'new',
        amount: orderTotal,
        currency: 'UZS',
        note: noteLines.join('\n'),
        message: noteLines.join('\n'),
        branchId: resolvedBranchId,
        deliveryEnabled: deliveryMode === 'delivery',
        items: effectiveCart.map((i) => ({
          product_id: Number(i.productId) || Number(String(i.id).split('__')[0]) || 0,
          name: i.name,
          quantity: i.qty,
          unit_price: i.salePrice,
          sale_unit: i.saleUnit ?? 'piece',
          unit_factor: i.lineUnitFactor ?? 1,
          ...(i.selectedChoiceKey ? { sale_option_key: i.selectedChoiceKey } : {}),
          ...(i.variantId != null &&
          String(i.variantId).trim() !== '' &&
          Number.isFinite(Number(i.variantId)) &&
          Number(i.variantId) > 0
            ? { variant_id: Number(i.variantId) }
            : {}),
        })),
        deliveryAddress: deliveryMode === 'delivery' ? address.trim() : null,
      } as any);
      window.dispatchEvent(
        new CustomEvent('marketing:order-created', {
          detail: {
            id: String(created?.id ?? `tmp-${Date.now()}`),
            number: String(created?.number ?? created?.id ?? `tmp-${Date.now()}`),
            source: 'marketing_web',
            branchId: String(resolvedBranchId || ''),
            branchName: branchName || '',
            clientName: name.trim(),
            clientPhone: orderPhone,
            region: regionName || '',
            district: districtName || '',
            note: noteLines.join('\n'),
            status: 'new',
            statusLabel: t('adminMarketing_status_new'),
            createdAt: new Date().toISOString(),
            deliveryAddress: deliveryMode === 'delivery' ? address.trim() : '',
            items: effectiveCart.map((i) => ({
              product_id: i.productId,
              name: i.name,
              barcode: i.barcode ?? '',
              quantity: i.qty,
              unit_price: i.salePrice,
              subtotal: i.qty * i.salePrice,
              ...(i.variantLabel ? { size_label: i.variantLabel } : {}),
              ...(i.selectedChoiceKey ? { sale_option_key: i.selectedChoiceKey } : {}),
              ...(i.saleUnit ? { sale_unit: i.saleUnit } : {}),
              ...(i.variantLabel ? { variant_summary: i.variantLabel } : {}),
            })),
          },
        }),
      );
      setOk(t('branchShop_order_success'));
      setCart([]);
      setName('');
      setPhoneLocal('');
      setRegionId('');
      setDistrictId('');
      setAddress('');
      setDistricts([]);
      setSmsCode('');
      setVerificationCode('');
      setVerificationId('');
      setVerifyToken('');
      setSecondsLeft(0);
      setSmsRequested(false);
      setSmsVerified(false);
      setSmsError('');
      setCartModalOpen(false);
    } catch (err: any) {
      const errBody = err?.data ?? err?.response?.data ?? {};
      const code = pickApiErrorCode(errBody);
      const serverMsg = pickFirstPresent(errBody, ['message', 'error', 'msg']);
      if (code === 'INSUFFICIENT_STOCK') {
        setSubmitError(
          serverMsg || t('branchShop_insufficient_stock'),
        );
      } else {
        setSubmitError(
          mapSmsErrorCodeToMessage(code, t) ||
            serverMsg ||
            err?.message ||
            t('branchShop_order_failed'),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const checkoutBlock = (
    <>
      <div className="branch-shop-total">{t('branchShop_total')}: {formatUzs(total)}</div>
      <label className="branch-shop-field">
        <span>{t('branchShop_client_name')}</span>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('branchShop_client_name_ph')} autoComplete="name" />
      </label>
      <label className="branch-shop-field">
        <span>{t('branchShop_phone')}</span>
        <div className="branch-shop-phone-wrap">
          <span className="branch-shop-phone-prefix">+998</span>
          <input
            className="branch-shop-phone-input"
            value={formatLocalPhoneDisplay(phoneLocal)}
            onChange={(e) => {
              setPhoneLocal(parsePhoneLocalInput(e.target.value));
              setSmsRequested(false);
              setSmsVerified(false);
              setSmsCode('');
              setVerificationCode('');
              setVerificationId('');
              setVerifyToken('');
              setSecondsLeft(0);
            }}
            placeholder="50 777 76 67"
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            aria-label={t('branchShop_phone_aria')}
            maxLength={14}
          />
        </div>
      </label>
      <label className="branch-shop-field">
        <span>{t('branchShop_delivery_method')}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className={`branch-shop-add-btn ${deliveryMode === 'pickup' ? 'is-active' : ''}`} onClick={() => setDeliveryMode('pickup')}>
            {t('branchShop_pickup')}
          </button>
          <button type="button" className={`branch-shop-add-btn ${deliveryMode === 'delivery' ? 'is-active' : ''}`} onClick={() => setDeliveryMode('delivery')}>
            {t('branchShop_delivery')}
          </button>
        </div>
      </label>
      {deliveryMode === 'delivery' && (
      <>
      <label className="branch-shop-field">
        <span>{t('branchShop_region')}</span>
        <Select
          value={regionId}
          onChange={(v) => {
            setRegionId(v);
            setDistrictId('');
            setSmsRequested(false);
            setSmsVerified(false);
            setVerifyToken('');
          }}
          disabled={regionsLoading}
          options={[
            { value: '', label: regionsLoading ? t('branchShop_loading') : t('branchShop_region_select') },
            ...regions.map((r) => ({ value: String(r.id), label: String(r.name) })),
          ]}
        />
      </label>
      <label className="branch-shop-field">
        <span>{t('branchShop_district')}</span>
        <Select
          value={districtId}
          onChange={(v) => {
            setDistrictId(v);
            setSmsRequested(false);
            setSmsVerified(false);
            setVerifyToken('');
          }}
          disabled={!regionId || districtsLoading}
          options={[
            {
              value: '',
              label: !regionId ? t('branchShop_district_select_first') : districtsLoading ? t('branchShop_loading') : t('branchShop_district_select'),
            },
            ...districts.map((d) => ({ value: String(d.id), label: String(d.name) })),
          ]}
        />
      </label>
      <label className="branch-shop-field">
        <span>{t('branchShop_address')}</span>
        <input
          value={address}
          onChange={(e) => {
            setAddress(e.target.value);
            setSmsRequested(false);
            setSmsVerified(false);
            setVerifyToken('');
          }}
          placeholder={t('branchShop_address_ph')}
        />
      </label>
      </>
      )}
      {smsRequested && (
        <div className="branch-shop-sms-block">
          {verificationCode ? (
            <div className="branch-shop-code-card" role="region" aria-label={t('branchShop_verify_code_title')}>
              <div className="branch-shop-code-card-top">
                <div>
                  <div className="branch-shop-code-card-title">{t('branchShop_verify_code_title')}</div>
                  <p className="branch-shop-code-card-desc">{t('branchShop_verify_code_desc')}</p>
                </div>
                <span className="branch-shop-code-pill">{t('branchShop_test_badge')}</span>
              </div>
              <div className="branch-shop-code-digits" aria-live="polite">
                {verificationCode.split('').map((ch, i) => (
                  <span key={i} className="branch-shop-code-digit">
                    {ch}
                  </span>
                ))}
              </div>
              <div className="branch-shop-code-actions">
                <button type="button" className="branch-shop-code-copy" onClick={() => void copyVerificationCode()}>
                  {codeCopied ? t('branchShop_copied') : t('branchShop_copy_code')}
                </button>
              </div>
            </div>
          ) : null}
          <label className="branch-shop-field branch-shop-field-sms-input">
            <span>{t('branchShop_sms_code')}</span>
            <input
              value={smsCode}
              onChange={(e) => {
                setSmsCode(e.target.value.replace(/\D/g, '').slice(0, SMS_OTP_LENGTH));
                setSmsVerified(false);
              }}
              placeholder="0000"
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          </label>
        </div>
      )}
      {smsRequested && secondsLeft > 0 ? <p className="branch-shop-ok">{t('branchShop_resend_in')}: {formatMmSs(secondsLeft)}</p> : null}
      {smsRequested && !smsVerified ? (
        <button
          type="button"
          className="branch-shop-btn-primary"
          disabled={submitting || smsSending}
          onClick={() => void handleConfirmPhone()}
        >
          {smsSending ? t('branchShop_sms_sending') : t('branchShop_verify_phone')}
        </button>
      ) : (
        <button
          type="button"
          className="branch-shop-btn-primary"
          disabled={submitting || smsSending}
          onClick={() => void submit()}
        >
          {submitting
            ? t('branchShop_submitting')
            : smsSending
              ? t('branchShop_sms_sending')
              : !smsRequested
                ? t('branchShop_send_sms')
                : t('branchShop_place_order')}
        </button>
      )}
      {smsVerified && !submitError ? <p className="branch-shop-ok">{t('branchShop_phone_verified')}</p> : null}
      {smsError ? <p className="branch-shop-error">{smsError}</p> : null}
      {ok ? <p className="branch-shop-ok">{ok}</p> : null}
      {submitError ? <p className="branch-shop-error">{submitError}</p> : null}
    </>
  );

  return (
    <div className="branch-shop">
      <header className="branch-shop-head">
        <div className="branch-shop-head-row">
          <div className="branch-shop-head-text">
            <h1>{branchName || t('branchShop_default_title')}</h1>
            <p>{t('branchShop_subtitle')}</p>
          </div>
          <LanguageDropdown className="branch-shop-lang" ariaLabel={t('adminSettings_language') || 'Language'} />
          <button
            type="button"
            className="branch-shop-cart-fab"
            aria-label={t('branchShop_cart_aria')}
            onClick={() => setCartModalOpen(true)}
          >
            <svg className="branch-shop-cart-icon" viewBox="0 0 24 24" width="24" height="24" aria-hidden>
              <path
                fill="currentColor"
                d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.15.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"
              />
            </svg>
            {cartCount > 0 ? <span className="branch-shop-cart-badge">{cartCount > 99 ? '99+' : cartCount}</span> : null}
          </button>
        </div>
      </header>

      {loading ? (
        <div className="branch-shop-loading">
          <span className="branch-shop-spinner" aria-hidden />
          <p>{t('branchShop_loading')}</p>
        </div>
      ) : (
        <div className="branch-shop-grid">
          <section className="branch-shop-products">
            {products.length > 0 ? (
              <label className="branch-shop-product-search">
                <span className="branch-shop-product-search-label">{t('branchShop_search_label')}</span>
                <input
                  type="search"
                  className="branch-shop-product-search-input"
                  placeholder={t('branchShop_search_ph')}
                  value={productFilterQuery}
                  onChange={(e) => setProductFilterQuery(e.target.value)}
                  autoComplete="off"
                />
              </label>
            ) : null}
            {products.length === 0 ? (
              <p className="branch-shop-empty">{t('branchShop_no_products')}</p>
            ) : filteredProducts.length === 0 ? (
              <p className="branch-shop-empty">{t('branchShop_no_search_results')}</p>
            ) : (
              filteredProducts.map((p) => {
                const img = resolveMediaUrl(p.imageUrl);
                return (
                  <article key={p.id} className="branch-shop-card">
                    <div className="branch-shop-image-wrap">
                      {img ? (
                        <img src={img} alt="" className="branch-shop-image" loading="lazy" />
                      ) : (
                        <div className="branch-shop-no-image">{t('branchShop_no_image')}</div>
                      )}
                    </div>
                    <h3>{p.name}</h3>
                    {p.marketingTitle ? <p className="branch-shop-marketing-title">{p.marketingTitle}</p> : null}
                    <p className="branch-shop-price">{formatUzs(p.salePrice)}</p>
                    {Array.isArray(p.saleChoices) && p.saleChoices.length > 0 && (
                      <div className="branch-shop-choices-preview">
                        {p.saleChoices.slice(0, 5).map((sc) => {
                          const hint = saleChoiceFactorHint(sc, t);
                          return (
                            <span key={sc.key} className="branch-shop-choice-tag">
                              {saleChoicePrimaryTitle(sc, t)}
                              {hint ? ` · ${hint}` : ''}
                            </span>
                          );
                        })}
                        {p.saleChoices.length > 5 && (
                          <span className="branch-shop-choice-tag branch-shop-choice-tag--more">+{p.saleChoices.length - 5}</span>
                        )}
                      </div>
                    )}
                    <button type="button" className="branch-shop-add-btn" onClick={() => handleAddToCart(p)}>
                      {t('branchShop_add_to_cart')}
                    </button>
                  </article>
                );
              })
            )}
          </section>

          <aside className="branch-shop-cart branch-shop-cart-aside" aria-label={t('branchShop_cart_aria')}>
            <h2 className="branch-shop-cart-title">{t('branchShop_cart_title')}</h2>
            {cart.length === 0 ? (
              <p className="branch-shop-cart-empty">{t('branchShop_cart_empty')}</p>
            ) : (
              <ul className="branch-shop-cart-list">
                {cart.map((i) => (
                  <li key={i.id} className="branch-shop-cart-line">
                    <div className="branch-shop-cart-line-name">
                      {i.name}
                      {i.variantLabel && (
                        <span className="branch-shop-cart-variant-label"> — {i.variantLabel}</span>
                      )}
                    </div>
                    <input
                      className="branch-shop-qty"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      aria-label={t('branchShop_qty_aria')}
                      title={i.maxStock > 0 ? fillTpl(t('branchShop_qty_max'), { n: i.maxStock }) : undefined}
                      value={qtyDraftByLineId[i.id] !== undefined ? qtyDraftByLineId[i.id] : String(i.qty)}
                      onChange={(e) => setQtyDraftByLineId((d) => ({ ...d, [i.id]: e.target.value }))}
                      onBlur={(e) => {
                        const raw = e.target.value;
                        setQtyDraftByLineId((d) => {
                          const n = { ...d };
                          delete n[i.id];
                          return n;
                        });
                        setCart((prev) =>
                          prev.map((x) => {
                            if (x.id !== i.id) return x;
                            const trimmed = String(raw).trim();
                            const parsed = trimmed === '' ? NaN : parseFloat(trimmed.replace(',', '.'));
                            const qBase = Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1;
                            return { ...x, qty: clampCartLineQty(x, qBase) };
                          }),
                        );
                      }}
                    />
                    <button
                      type="button"
                      className="branch-shop-line-remove"
                      onClick={() => removeLine(i.id)}
                      aria-label={t('branchShop_remove_from_cart')}
                    >
                      ×
                    </button>
                    <div className="branch-shop-cart-line-sum">{formatUzs(i.qty * i.salePrice)}</div>
                  </li>
                ))}
              </ul>
            )}
            {checkoutBlock}
          </aside>
        </div>
      )}

      {cartModalOpen && (
        <div
          className="branch-shop-modal-backdrop"
          role="presentation"
          onClick={() => setCartModalOpen(false)}
        >
          <div
            className="branch-shop-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="branch-shop-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="branch-shop-modal-head">
              <h2 id="branch-shop-modal-title">{t('branchShop_cart_title')}</h2>
              <button type="button" className="branch-shop-modal-close" onClick={() => setCartModalOpen(false)} aria-label={t('branchShop_close')}>
                ×
              </button>
            </div>
            {cart.length === 0 ? (
              <p className="branch-shop-cart-empty">{t('branchShop_cart_empty')}</p>
            ) : (
              <ul className="branch-shop-cart-list branch-shop-cart-list-modal">
                {cart.map((i) => (
                  <li key={i.id} className="branch-shop-cart-line branch-shop-cart-line-modal">
                    <div className="branch-shop-cart-line-name">{i.name}</div>
                    <input
                      className="branch-shop-qty"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      aria-label={t('branchShop_qty_aria')}
                      title={i.maxStock > 0 ? fillTpl(t('branchShop_qty_max'), { n: i.maxStock }) : undefined}
                      value={qtyDraftByLineId[i.id] !== undefined ? qtyDraftByLineId[i.id] : String(i.qty)}
                      onChange={(e) => setQtyDraftByLineId((d) => ({ ...d, [i.id]: e.target.value }))}
                      onBlur={(e) => {
                        const raw = e.target.value;
                        setQtyDraftByLineId((d) => {
                          const n = { ...d };
                          delete n[i.id];
                          return n;
                        });
                        setCart((prev) =>
                          prev.map((x) => {
                            if (x.id !== i.id) return x;
                            const trimmed = String(raw).trim();
                            const parsed = trimmed === '' ? NaN : parseFloat(trimmed.replace(',', '.'));
                            const qBase = Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1;
                            return { ...x, qty: clampCartLineQty(x, qBase) };
                          }),
                        );
                      }}
                    />
                    <button
                      type="button"
                      className="branch-shop-line-remove"
                      onClick={() => removeLine(i.id)}
                      aria-label={t('branchShop_remove_from_cart')}
                    >
                      ×
                    </button>
                    <div className="branch-shop-cart-line-sum">{formatUzs(i.qty * i.salePrice)}</div>
                  </li>
                ))}
              </ul>
            )}
            {checkoutBlock}
          </div>
        </div>
      )}
      {/* variant picker modal */}
      {variantPickerProduct && (
        <div className="branch-shop-variant-picker-overlay" onClick={() => setVariantPickerProduct(null)}>
          <div className="branch-shop-variant-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="branch-shop-variant-picker-header">
              <span className="branch-shop-variant-picker-title">{variantPickerProduct.name}</span>
              <button className="branch-shop-variant-picker-close" onClick={() => setVariantPickerProduct(null)}>✕</button>
            </div>
            <p className="branch-shop-variant-picker-hint">{t('branchShop_variant_hint')}</p>
            <div className="branch-shop-variant-picker-grid">
              {(variantPickerProduct.saleChoices ?? []).map((sc, idx) => {
                const factorHint = saleChoiceFactorHint(sc, t);
                return (
                  <button
                    key={`${sc.saleUnit}-${sc.key}-${idx}`}
                    type="button"
                    className="branch-shop-variant-btn"
                    onClick={() => {
                      addToCart(variantPickerProduct, sc);
                      setVariantPickerProduct(null);
                    }}
                  >
                    <span className="branch-shop-variant-btn-label">{saleChoicePrimaryTitle(sc, t)}</span>
                    {factorHint ? <span className="branch-shop-variant-btn-factor">{factorHint}</span> : null}
                    <span className="branch-shop-variant-btn-price">{formatMoneyUz(sc.price ?? 0)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
